import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import { Button } from '@/components/ui';
import type { ReportComment } from '@/types/dailyReports.types';

const EXCERPT_LENGTH = 220;

interface ReportCommentsCarouselProps {
  comments: ReportComment[];
  onReadAll: (comment: ReportComment) => void;
}

export function ReportCommentsCarousel({ comments, onReadAll }: ReportCommentsCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const scrollSettleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goTo = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(comments.length - 1, index));
      setCurrentIndex(clamped);
      const el = scrollerRef.current;
      if (el) {
        el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
      }
    },
    [comments.length]
  );

  useEffect(() => {
    setCurrentIndex(0);
    const el = scrollerRef.current;
    if (el) el.scrollTo({ left: 0 });
  }, [comments]);

  const handlePrevious = useCallback(() => {
    goTo(currentIndex - 1);
  }, [currentIndex, goTo]);

  const handleNext = useCallback(() => {
    goTo(currentIndex + 1);
  }, [currentIndex, goTo]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(currentIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, goTo]);

  const onScroll = useCallback(() => {
    if (scrollSettleRef.current) clearTimeout(scrollSettleRef.current);
    scrollSettleRef.current = setTimeout(() => {
      const el = scrollerRef.current;
      if (!el || comments.length === 0) return;
      const w = el.clientWidth;
      if (w <= 0) return;
      const i = Math.round(el.scrollLeft / w);
      const clamped = Math.max(0, Math.min(comments.length - 1, i));
      setCurrentIndex((prev) => (clamped !== prev ? clamped : prev));
    }, 80);
  }, [comments.length]);

  if (comments.length === 0) {
    return null;
  }

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === comments.length - 1;

  return (
    <div className="overflow-hidden rounded-lg border border-amber-200/70 bg-gradient-to-b from-amber-50/95 to-amber-100/40 shadow-sm dark:border-amber-900/35 dark:from-amber-950/25 dark:to-amber-950/10">
      <div className="flex items-center justify-between gap-2 border-b border-amber-200/50 bg-amber-50/60 px-3 py-2 dark:border-amber-900/30 dark:bg-amber-950/20 sm:px-4">
        <h2 className="min-w-0 truncate text-sm font-semibold capitalize text-amber-950 dark:text-amber-100 sm:text-base">
          Notes
        </h2>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrevious}
            disabled={isFirst}
            className={clsx('!h-9 !min-w-9 !px-0', isFirst && 'pointer-events-none invisible')}
            aria-label="Previous note"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <span className="min-w-[2.75rem] text-center text-xs tabular-nums text-amber-800/70 dark:text-amber-200/70 sm:text-sm">
            {currentIndex + 1} / {comments.length}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNext}
            disabled={isLast}
            className={clsx('!h-9 !min-w-9 !px-0', isLast && 'pointer-events-none invisible')}
            aria-label="Next note"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="grid max-w-full snap-x snap-mandatory auto-cols-[100%] grid-flow-col overflow-x-auto overscroll-x-contain scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="region"
        aria-roledescription="carousel"
        aria-label="Commentaries"
      >
        {comments.map((comment, index) => {
          const formattedDate = format(new Date(comment.date), 'EEE, MMM d, yyyy · h:mm a');
          const excerpt =
            comment.comment.length > EXCERPT_LENGTH
              ? comment.comment.substring(0, EXCERPT_LENGTH) + '…'
              : comment.comment;
          const isActive = index === currentIndex;

          return (
            <div
              key={`${comment.date}-${index}`}
              className="snap-center snap-always px-3 pb-3 pt-2 sm:px-4 sm:pb-4 sm:pt-3"
              aria-hidden={!isActive}
            >
              <article
                className={clsx(
                  'mx-auto max-w-2xl rounded-md border border-amber-200/60 bg-gradient-to-br from-[#fdfbf7] via-[#fcf8f0] to-[#f8f2e8] px-4 py-3 shadow-sm sm:px-5 sm:py-4',
                  'dark:border-amber-800/40 dark:from-amber-950/40 dark:via-amber-950/25 dark:to-stone-950',
                  isActive && 'ring-1 ring-amber-400/25 dark:ring-amber-500/20'
                )}
              >
                <header className="border-b border-amber-900/10 pb-2 dark:border-amber-700/25">
                  <time
                    dateTime={comment.date}
                    className="font-serif text-xs font-medium text-amber-900/65 dark:text-amber-200/75"
                  >
                    {formattedDate}
                  </time>
                </header>
                <div className="mt-2.5 font-serif text-sm leading-relaxed text-amber-950/95 dark:text-amber-50/95 sm:text-[0.9375rem] sm:leading-relaxed">
                  <p className="whitespace-pre-wrap">{excerpt}</p>
                  <p className="mt-3">
                    <button
                      type="button"
                      onClick={() => onReadAll(comment)}
                      className="text-sm font-medium text-primary-600 underline decoration-primary-600/40 underline-offset-2 transition-colors hover:text-primary-700 hover:decoration-primary-600 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      Read all
                    </button>
                  </p>
                </div>
              </article>
            </div>
          );
        })}
      </div>

      <div className="flex justify-center gap-1.5 pb-3 pt-1" role="tablist" aria-label="Note pages">
        {comments.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            className={clsx(
              'h-1.5 rounded-full transition-all',
              i === currentIndex
                ? 'w-6 bg-primary-600 dark:bg-primary-500'
                : 'w-1.5 bg-amber-300/80 hover:bg-amber-400/90 dark:bg-amber-800/50 dark:hover:bg-amber-700/60'
            )}
            aria-label={`Go to note ${i + 1}`}
            aria-current={i === currentIndex ? 'true' : undefined}
          />
        ))}
      </div>
    </div>
  );
}
